import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, inr } from "../../lib/api";
import { toast } from "sonner";
import { MapPin, Truck, Wallet, ChevronRight, Filter, X, Undo2 } from "lucide-react";
import AdminShopPicker from "../../components/AdminShopPicker";

const STATUS_PILLS = [
    { key: "", label: "All" },
    { key: "PLACED", label: "Placed" },
    { key: "CONFIRMED", label: "Confirmed" },
    { key: "PREPARING", label: "Preparing" },
    { key: "READY", label: "Ready" },
    { key: "OUT_FOR_DELIVERY", label: "On the way" },
    { key: "DELIVERED", label: "Delivered" },
    { key: "CANCELLED", label: "Cancelled" },
];

const STATUS_SEQ = ["PLACED", "CONFIRMED", "PREPARING", "READY", "OUT_FOR_DELIVERY", "DELIVERED", "CANCELLED"];

function todayIso() { return new Date().toISOString().slice(0, 10); }
function yesterdayIso() { const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); }

export default function AdminOrders() {
    const [rows, setRows] = useState([]);
    const [restaurants, setRestaurants] = useState([]);
    const [zones, setZones] = useState([]);
    const [status, setStatus] = useState("");
    const [restaurantId, setRestaurantId] = useState("");
    const [zoneId, setZoneId] = useState("");
    const [datePreset, setDatePreset] = useState(""); // "today" | "yesterday" | "custom" | ""
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        api.get("/admin/restaurants").then(({ data }) => setRestaurants(data || []));
        api.get("/admin/zones").then(({ data }) => setZones((data || []).filter(z => z.status !== "DELETED")));
    }, []);

    const params = useMemo(() => {
        const p = new URLSearchParams();
        if (status) p.set("status", status);
        if (restaurantId) p.set("restaurant_id", restaurantId);
        if (zoneId) p.set("zone_id", zoneId);
        if (dateFrom) p.set("date_from", dateFrom);
        if (dateTo) p.set("date_to", dateTo);
        return p.toString();
    }, [status, restaurantId, zoneId, dateFrom, dateTo]);

    const load = async () => {
        try {
            const { data } = await api.get(`/admin/orders${params ? `?${params}` : ""}`);
            setRows(data);
        } catch { /* ignore */ }
    };
    useEffect(() => { load(); const i = setInterval(load, 8000); return () => clearInterval(i); /* eslint-disable-next-line */ }, [params]);

    const setDateShortcut = (kind) => {
        setDatePreset(kind);
        if (kind === "today") { const d = todayIso(); setDateFrom(d); setDateTo(d); }
        else if (kind === "yesterday") { const d = yesterdayIso(); setDateFrom(d); setDateTo(d); }
        else if (kind === "") { setDateFrom(""); setDateTo(""); }
    };

    const advance = async (oid, next) => {
        try {
            await api.patch(`/admin/orders/${oid}/status?status=${next}`);
            toast.success(`→ ${next.replace("_", " ")}`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };

    const restById = useMemo(() => Object.fromEntries(restaurants.map(r => [r.id, r])), [restaurants]);
    const filterCount = (restaurantId ? 1 : 0) + (zoneId ? 1 : 0) + (datePreset && datePreset !== "" ? 1 : 0);

    return (
        <div>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold">Orders</h1>
                    <p className="text-sm text-muted-foreground mt-1">Live queue • {rows.length} shown • auto-refresh 8s</p>
                </div>
                <button data-testid="orders-filters-toggle" onClick={() => setShowFilters(v => !v)}
                    className={`inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-xl border ${filterCount ? "bg-primary/10 text-primary border-primary/30" : "border-border"}`}>
                    <Filter size={14} /> Filters{filterCount > 0 && <span className="bg-primary text-primary-foreground px-1.5 rounded-full text-[10px]">{filterCount}</span>}
                </button>
            </div>

            {/* Status pills */}
            <div className="mt-4 flex items-center gap-2 flex-wrap">
                {STATUS_PILLS.map((s) => (
                    <button key={s.key || "all"} data-testid={`status-${s.key || "ALL"}`} onClick={() => setStatus(s.key)}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${status === s.key ? "bg-foreground text-background border-foreground" : "border-border"}`}>
                        {s.label}
                    </button>
                ))}
            </div>

            {/* Filters drawer */}
            {showFilters && (
                <div data-testid="orders-filters" className="mt-4 bg-card border border-border rounded-2xl p-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                    <label className="flex flex-col gap-1">
                        <span className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Shop</span>
                        <AdminShopPicker
                            testIdPrefix="filter-restaurant"
                            value={restaurantId}
                            onChange={setRestaurantId}
                            allShopsOption
                            shopsOverride={restaurants}
                            placeholder="All restaurants"
                        />
                    </label>
                    <label className="flex flex-col gap-1">
                        <span className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Zone</span>
                        <select data-testid="filter-zone" className="inp" value={zoneId} onChange={(e) => setZoneId(e.target.value)}>
                            <option value="">All zones</option>
                            {zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
                        </select>
                    </label>
                    <div className="flex flex-col gap-1 sm:col-span-2">
                        <span className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Date</span>
                        <div className="flex items-center gap-2 flex-wrap">
                            {[
                                { k: "", l: "Any" },
                                { k: "today", l: "Today" },
                                { k: "yesterday", l: "Yesterday" },
                                { k: "custom", l: "Custom range" },
                            ].map(opt => (
                                <button key={opt.k || "any"} data-testid={`date-${opt.k || "ANY"}`} onClick={() => setDateShortcut(opt.k)}
                                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${datePreset === opt.k ? "bg-foreground text-background border-foreground" : "border-border"}`}>
                                    {opt.l}
                                </button>
                            ))}
                            {datePreset === "custom" && (
                                <div className="inline-flex items-center gap-2">
                                    <input data-testid="date-from" type="date" className="inp max-w-[160px]" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                                    <span className="text-muted-foreground">to</span>
                                    <input data-testid="date-to" type="date" className="inp max-w-[160px]" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                                </div>
                            )}
                        </div>
                    </div>
                    {filterCount > 0 && (
                        <div className="sm:col-span-2 lg:col-span-4">
                            <button data-testid="clear-filters" onClick={() => { setRestaurantId(""); setZoneId(""); setDateShortcut(""); }}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-destructive">
                                <X size={12} /> Clear all
                            </button>
                        </div>
                    )}
                    <style>{`.inp{background:hsl(var(--muted));border-radius:0.75rem;padding:0.55rem 0.75rem;outline:none;font-size:0.875rem;width:100%}`}</style>
                </div>
            )}

            {/* List */}
            <div className="mt-6 space-y-3">
                {rows.map((o) => {
                    const next = STATUS_SEQ[STATUS_SEQ.indexOf(o.status) + 1];
                    const advanceable = next && o.status !== "DELIVERED" && o.status !== "CANCELLED";
                    const rest = restById[o.restaurant_id];
                    const cdc = !!(o.cdc_enabled ?? rest?.cdc_enabled);
                    return (
                        <div key={o.id} data-testid={`order-row-${o.id}`} className="bg-card border border-border rounded-2xl p-4">
                            <div className="flex items-center gap-3 flex-wrap">
                                <div className="flex-1 min-w-[200px]">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <p className="font-bold text-sm">{o.order_unique_id}</p>
                                        <StatusBadge status={o.status} />
                                        {o.zone_name && <span data-testid={`zone-badge-${o.id}`} className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 inline-flex items-center gap-1"><MapPin size={10} />{o.zone_name}</span>}
                                        {cdc && <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">CDC</span>}
                                        {o.return_status && <ReturnBadge status={o.return_status} orderId={o.id} />}
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-1">{o.customer_name || o.customer_mobile} • {o.restaurant_name}</p>
                                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{(o.items || []).map((i) => `${i.name} ×${i.quantity}`).join(", ")}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm font-bold">{inr(o.totals?.grand_total)}</p>
                                    {cdc ? (
                                        <p className="text-[11px] text-violet-700 font-semibold inline-flex items-center gap-1"><Truck size={11} /> Pay restaurant {inr(o.totals?.merchant_payout || 0)}</p>
                                    ) : o.payment_method === "COD" ? (
                                        <p className="text-[11px] text-amber-700 font-semibold inline-flex items-center gap-1"><Wallet size={11} /> Collect {inr(o.totals?.grand_total || 0)}</p>
                                    ) : (
                                        <p className="text-[11px] text-emerald-700 font-semibold">Prepaid</p>
                                    )}
                                </div>
                                <Link to={`/admin/orders/${o.id}`} data-testid={`order-open-${o.id}`} className="text-xs font-bold text-primary inline-flex items-center gap-1">Details <ChevronRight size={12} /></Link>
                                {advanceable && (
                                    <button data-testid={`advance-${o.id}`} onClick={() => advance(o.id, next)} className="bg-primary text-primary-foreground rounded-xl px-3 py-1.5 text-xs font-bold">→ {next.replace("_", " ")}</button>
                                )}
                            </div>
                        </div>
                    );
                })}
                {rows.length === 0 && <p className="text-center py-12 text-muted-foreground">No orders match these filters.</p>}
            </div>
        </div>
    );
}

/**
 * A return is a side record on the order, not an order status (see
 * models.OrderReturn), so it needs its own pill — without it the Orders
 * queue looked identical whether or not a customer had filed a return,
 * and the only way to find out was the separate Returns page.
 */
function ReturnBadge({ status, orderId }) {
    const map = {
        PENDING: "bg-amber-100 text-amber-800",
        APPROVED: "bg-emerald-100 text-emerald-700",
        REJECTED: "bg-red-100 text-red-700",
    };
    const labels = {
        PENDING: "Return pending",
        APPROVED: "Return approved",
        REJECTED: "Return rejected",
    };
    return (
        <span
            data-testid={`return-badge-${orderId}`}
            title={labels[status] || `Return ${status}`}
            className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${map[status] || "bg-muted text-muted-foreground"}`}
        >
            <Undo2 size={10} />{labels[status] || `Return ${status}`}
        </span>
    );
}

function StatusBadge({ status }) {
    const map = {
        DELIVERED: "bg-emerald-100 text-emerald-700",
        CANCELLED: "bg-red-100 text-red-700",
        REJECTED: "bg-red-100 text-red-700",
        PLACED: "bg-amber-100 text-amber-700",
        CONFIRMED: "bg-amber-100 text-amber-700",
        PREPARING: "bg-orange-100 text-orange-700",
        READY: "bg-blue-100 text-blue-700",
        PICKED_UP: "bg-indigo-100 text-indigo-700",
        OUT_FOR_DELIVERY: "bg-indigo-100 text-indigo-700",
    };
    const cls = map[status] || "bg-muted text-muted-foreground";
    return <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${cls}`}>{(status || "").replace("_", " ")}</span>;
}
